import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  showCloseButton?: boolean;
  className?: string;
  /**
   * 'center' (Default) ist der zentrierte Dialog auf weissem Grund.
   * 'sheet' blendet von unten ein und nutzt die bestehenden
   * bottom-sheet-Klassen aus styles/index.css - dieselbe Optik wie die
   * Premium-Abfrage im Produktcheck (ScannerPage).
   */
  variant?: 'center' | 'sheet';
}

/**
 * Zentriertes Basis-Modal.
 *
 * Uebernimmt das Muster der bestehenden Modals (createPortal, Early Return
 * bei !isOpen, X-Icon aus lucide-react) und ergaenzt das, was dort bisher
 * jedes Modal fuer sich loesen musste: Escape, Klick auf den Backdrop und
 * Scroll-Lock des Bodys.
 *
 * Die 17 bestehenden Modals bleiben vorerst unveraendert.
 */
export function Modal({
  isOpen,
  onClose,
  children,
  title,
  showCloseButton = true,
  className,
  variant = 'center',
}: ModalProps) {
  // Escape schliesst
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Body-Scroll sperren, solange das Modal offen ist
  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  if (variant === 'sheet') {
    return createPortal(
      <>
        <div className="bottom-sheet-backdrop" onClick={onClose} role="presentation" />
        {/* Der Container deckt auf Desktop die ganze Flaeche ab, deshalb
            schliesst auch ein Klick hier - sonst waere der Rand tot. */}
        <div className="bottom-sheet-container" onClick={onClose} role="presentation">
          <div
            className={cn('bottom-sheet-modal', className)}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            // Safe-Area-Inset unten, damit der Inhalt auf iOS nicht in der
            // Home-Indicator-Zone endet
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
          >
            {/* Drag-Indikator - ab sm wird das Sheet zum zentrierten
                Dialog (siehe styles/index.css), dort waere er sinnlos */}
            <div className="flex-shrink-0 flex justify-center pt-2 pb-1 sm:hidden">
              <div className="h-1 w-10 rounded-full bg-white/30" />
            </div>

            {(title || showCloseButton) && (
              <div className="bottom-sheet-header">
                <h2>{title}</h2>
                {showCloseButton && (
                  <button
                    onClick={onClose}
                    aria-label="Schliessen"
                    className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            )}

            {children}
          </div>
        </div>
      </>,
      document.body
    );
  }

  const modalContent = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={cn(
          'relative bg-white rounded-2xl max-w-sm w-full max-h-[90vh] overflow-y-auto',
          className
        )}
        // Klicks im Inhalt duerfen nicht bis zum Backdrop laufen
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {title && (
          <h2 className="text-lg font-bold text-gray-900 px-6 pt-6 pr-14">{title}</h2>
        )}

        {showCloseButton && (
          <button
            onClick={onClose}
            aria-label="Schliessen"
            className="absolute top-3 right-3 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        {children}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
